import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ComboBoxDropDownItem, GroupedComboBoxDropDownItem } from "./Combobox";

interface IComboboxControlProps {
    id: string;
    onChange: (code: string) => void;
    items: Immutable.List<ComboBoxDropDownItem>;
    selectedCode: string;
    dataCy?: string;
    disabled?: boolean;
    hasEmptyItem?: boolean;
    className?: string;
    includeCodeInName?: boolean;
    style?: React.CSSProperties;
    maxNumberOptionCharacters?: number;
    showTooltips?: boolean;
    invalid?: boolean;
}

class ComboBoxControl extends BaseReactComponent<IComboboxControlProps> {
    public render(): JSX.Element {
        let items: Immutable.List<ComboBoxDropDownItem> = null;
        let groupedItems: Immutable.List<GroupedComboBoxDropDownItem> = null;

        if (this.props.items?.count() > 0) {
            items = this.props.items;
            groupedItems = items
                .groupBy(item => item.GroupName)
                .map((items, GroupName) => new GroupedComboBoxDropDownItem(GroupName, items.toList()))
                .sort((current, next) => {
                    const group1 = current.GroupName ?? "";
                    const group2 = next.GroupName ?? "";

                    return group1 === group2
                        ? 0
                        : group1.length > group2.length
                            ? -1
                            : 1;
                })
                .toList();

            if (this.props.hasEmptyItem && groupedItems.size > 0) {
                const emptyComboBox = new ComboBoxDropDownItem("", "", -1, null);
                items = items.push(emptyComboBox);

                if (groupedItems.get(0)) {
                    groupedItems.get(0).items = [
                        emptyComboBox,
                        ...groupedItems.toArray()[0]
                            .items
                            .map(x => x)
                            .toArray()
                    ].toImmutable();
                }
            }
        }

        const selectedItemCode = this.props.selectedCode ?? "";

        return (
            <select
                key={this.props.id + "_select"}
                data-cy={this.props.dataCy}
                style={{ ...this.props.style }}
                className={`form-control myle-control minimal ${this.props.className ?? ""} ${this.props.invalid ? "invalid" : ""}`}
                disabled={this.props.disabled ?? false}
                onChange={(e: any) => this.props.onChange?.(e.target.value)}
                title={this.props.items.find(p => p.Code === selectedItemCode)?.DisplayName ?? ""}
                value={selectedItemCode}
            >
                {groupedItems?.count() > 1 && groupedItems.map(group => (
                    <optgroup
                        key={this.props.id + "_" + group.GroupName}
                        label={group.GroupName}
                    >
                        {group.items != null &&
                            group.items.map((s: ComboBoxDropDownItem) => this.getComboBoxItemText(s, group))
                        }
                    </optgroup>
                ))}
                {groupedItems?.count() == 1
                    && groupedItems.map(group => group.items.map((item: ComboBoxDropDownItem) => this.getComboBoxItemText(item, group)))
                }
            </select>
        );
    }

    private getComboBoxItemText(s: ComboBoxDropDownItem, g: GroupedComboBoxDropDownItem): JSX.Element {
        const value = this.props.includeCodeInName && !s.Code
            ? (s.Code + " - " + s.DisplayName)
            : s.DisplayName;

        return (
            <option
                key={this.props.id + "_" + g.GroupName + "_" + s.Code}
                value={s.Code}
                title={this.props.showTooltips ? value : ""}
            >
                {this.props.maxNumberOptionCharacters && value.length > this.props.maxNumberOptionCharacters
                    ? limitTextLength(value, this.props.maxNumberOptionCharacters)
                    : value
                }
            </option>
        );
    }
}

export { ComboBoxControl };
